export const barColors = [
  "#FF6384", // Red
  "#36A2EB", // Blue
  "#FFCE56", // Yellow
  "#4BC0C0", // Teal
  "#9966FF", // Purple
  "#FF9F40", // Orange
];

export const object_category = {
  "Traffic Control Assets": {
    description: "Assets used to control traffic flow and improve safety at intersections and roadways.",
    imageSrc: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    color: "#FFEBEE", //background color
    items: [
      { name: "traffic light", type: "marker", items: [] },
      { name: "stop sign", type: "marker", items: [] },
      { name: "traffic cone", type: "marker", items: [] },
      { name: "traffic regulatory sign", type: "marker", items: [] },
      { name: "traffic warning sign", type: "marker", items: [] },
      { name: "pedestrian traffic light", type: "marker", items: [] }
    ]
  },
  "Parking Management Assets": {
    description: "Assets that designate and regulate parking availability along the curb.",
    imageSrc: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    color: "#E3F2FD",
    items: [
      { name: "parking sign", type: "marker", items: [] },
      { name: "temporary parking sign", type: "marker", items: [] }
    ]
  },
  "Safety and Emergency Assets": {
    description: "Infrastructure for public safety, emergency access, and physical protection.",
    imageSrc: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    color: "#e7f5e5",
    items: [
      { name: "fire hydrant", type: "marker", items: [] },
      { name: "bump", type: "marker", items: [] },
      { name: "permanent roadside barrier", type: "polyline", items: [] },
      { name: "temporary roadside barrier", type: "polyline", items: [] }
    ]
  }
};

/**
 * Converts a hex color to HSL.
 * @param {string} hex - The hex color code.
 * @returns {object} - An object with h, s, and l properties.
 */
const hexToHsl = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l;
  l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL to a hex color.
 * @param {number} h - Hue (0-360).
 * @param {number} s - Saturation (0-100).
 * @param {number} l - Lightness (0-100).
 * @returns {string} - The hex color code.
 */
const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
};

/**
 * Adjusts the brightness of a hex color using HSL.
 * @param {string} hex - The hex color code.
 * @param {number} amount - The amount to adjust the lightness (-ve for darker, +ve for lighter).
 * @returns {string} - The adjusted hex color code.
 */
export const adjustColorBrightness = (hex, amount) => {
  const { h, s, l } = hexToHsl(hex);
  const newL = Math.min(100, Math.max(0, l + amount));
  return hslToHex(h, s, newL);
};


/** Generate consistent and visually distinct colors for each bar. */
export const generateColorVariations = (baseColor, count) => {
  const variations = [];
  const step = 15; // Step to adjust lightness for each bar

  for (let i = 0; i < count; i++) {
    const adjustment = (i - Math.floor(count / 2)) * step; // Adjust lightness for each bar
    const adjustedColor = adjustColorBrightness(baseColor, adjustment);

    // Ensure the color is not too bright or too dark
    const { l } = hexToHsl(adjustedColor);
    if (l > 90) {
      variations.push(adjustColorBrightness(baseColor, -20)); // Darken overly bright colors
    } else if (l < 10) {
      variations.push(adjustColorBrightness(baseColor, 20)); // Lighten overly dark colors
    } else {
      variations.push(adjustedColor);
    }
  }

  return variations;
};
